<template>
    <nav :class="['navbar', `navbar--${variant}`]">
        <div class="nav-left">
            <div class="logo">
                <router-link to="/" class="logo-link">
                    <span class="company-name">Appointum</span>
                </router-link>
            </div>
        </div>

        <div v-if="this.variant == 'landing'">
            <!-- Mobile controls (sign-in + burger) -->
            <div class="mobile-controls">
                <router-link v-if="!isAuthenticated" to="/login" class="mobile-signin-btn">
                    Ienākt
                </router-link>
                <button class="mobile-menu-toggle" @click="toggleMobileMenu" :class="{ 'active': showMobileMenu }"
                    aria-label="Toggle menu">
                    <span class="hamburger-line"></span>
                    <span class="hamburger-line"></span>
                    <span class="hamburger-line"></span>
                </button>
            </div>

            <!-- Navigation links - shown on desktop, hidden/shown on mobile based on menu state -->
            <div class="nav-right" :class="{ 'mobile-open': showMobileMenu }">
                <a @click.prevent="goToSection('how-it-works')" href="#how-it-works" class="link-btn-no-underline">
                    Kā tas strādā
                </a>
                <a @click.prevent="goToSection('pricing')" href="#pricing" class="link-btn-no-underline">
                    Cenas
                </a>
                <a @click.prevent="goToSection('faq-section')" href="#faq-section" class="link-btn-no-underline">
                    BUJ
                </a>
                <router-link to="/contact-us" class="link-btn-no-underline">
                    Kontakti
                </router-link>
                <template v-if="!isAuthenticated">
                    <router-link to="/login" class="link-btn-no-underline">
                        Ienākt
                    </router-link>
                    <router-link to="/register" class="primary-btn" style="text-decoration:none">
                        Reģistrēties
                    </router-link>
                </template>
                <template v-else>
                    <router-link to="/workspace/dashboard/" class="primary-btn" style="text-decoration:none">
                        Uz kontrolpaneli
                    </router-link>
                </template>
            </div>
        </div>
    </nav>
</template>

<script>
import { useAuthStore } from '../stores/auth'

export default {
    name: 'NavigationBar',
    props: {
        variant: {
            type: String,
            default: 'compact',
            validator: value => ['landing', 'compact'].includes(value)
        }
    },
    setup() {
        const auth = useAuthStore()
        return { auth }
    },
    data() {
        return {
            showProfileMenu: false,
            showMobileMenu: false,
            clickOutsideHandlers: {},
            closeTimer: null
        }
    },
    computed: {
        isAuthenticated() {
            return this.auth.isAuthenticated
        },
        getUserPreferredName() {
            return this.auth.getPreferredName
        }
    },
    methods: {
        toggleMobileMenu() {
            this.showMobileMenu = !this.showMobileMenu;
        },
        goToSection(sectionId) {
            // Close mobile menu when navigating
            this.showMobileMenu = false;

            if (this.$route.path === '/') {
                // Already on landing page, scroll directly
                this.scrollToSection(sectionId);
            } else {
                // Navigate to landing page with hash, then scroll after route change
                this.$router.push({ path: '/', hash: `#${sectionId}` }).then(() => {
                    // Wait for the next tick and a bit more to ensure content is rendered
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.scrollToSection(sectionId);
                        }, 100); // Small delay to ensure DOM is ready
                    });
                });
            }
        },
        scrollToSection(sectionId) {
            this.$nextTick(() => {
                const element = document.getElementById(sectionId);
                if (element) {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            });
        },

        toggleProfileMenu() {
            this.showProfileMenu = !this.showProfileMenu;
        },
        handleClickOutside(event, menuKey) {
            const target = event.target;
            if (this.$refs[menuKey] && !this.$refs[menuKey].contains(target)) {
                this[menuKey === 'listingsDropdown' ? 'showListingsMenu' : 'showProfileMenu'] = false;
            }
        },
        startCloseTimer() {
            this.closeTimer = setTimeout(() => {
                this.showProfileMenu = false;
            }, 300);
        },
        clearCloseTimer() {
            if (this.closeTimer) {
                clearTimeout(this.closeTimer);
                this.closeTimer = null;
            }
        },
    },
    mounted() {
        this.clickOutsideHandlers = {
            profile: (e) => this.handleClickOutside(e, 'profile-section')
        };

        document.addEventListener('click', this.clickOutsideHandlers.profile);

        this.$router.afterEach(() => {
            this.showProfileMenu = false;
            this.showMobileMenu = false; // Close mobile menu on route change
        });
    },
    unmounted() {
        document.removeEventListener('click', this.clickOutsideHandlers.profile);
        this.clearCloseTimer();
    }
}
</script>

<style scoped>
/* Base navbar styles */
.navbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all 0.3s ease;
    position: relative;
}

.navbar--landing {
    margin-top: 40px;
}

.nav-left {
    display: flex;
    align-items: center;
    gap: 2rem;
}

.logo {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.company-name {
    font-weight: 500;
    color: var(--main-color);
    transition: font-size 0.3s ease;
}

.nav-right {
    display: flex;
    align-items: center;
    gap: 2rem;
}

.primary-btn {
    transition: padding 0.3s ease;
}

/* Mobile controls container - hidden on desktop */
.mobile-controls {
    display: none;
    align-items: center;
    gap: 12px;
}

/* Mobile sign-in button - hidden on desktop */
.mobile-signin-btn {
    display: none;
    padding: 6px 12px;
    font-size: 14px;
    color: var(--main-color);
    text-decoration: none;
    border: 1px solid var(--main-color);
    border-radius: 100px;
    transition: all 0.2s ease;
}

/* Mobile menu toggle button - hidden on desktop */
.mobile-menu-toggle {
    display: none;
    flex-direction: column;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    gap: 4px;
    z-index: 1001;
}

.hamburger-line {
    width: 24px;
    height: 1px;
    background-color: var(--main-color, #333);
    transition: all 0.3s ease;
    transform-origin: center;
}

.mobile-menu-toggle.active .hamburger-line:nth-child(1) {
    transform: rotate(45deg) translate(4px, 4px);
}

.mobile-menu-toggle.active .hamburger-line:nth-child(2) {
    opacity: 0;
}

.mobile-menu-toggle.active .hamburger-line:nth-child(3) {
    transform: rotate(-45deg) translate(4px, -4px);
}

/* Section navigation links styling */
.nav-right a[href^="#"] {
    cursor: pointer;
    transition: opacity 0.3s ease;
}

.nav-right a[href^="#"]:hover {
    opacity: 0.8;
}

/* Compact variant (default) */
.navbar--compact {
    height: 84px;
    padding-left: 3rem;
}

.navbar--compact .company-name {
    font-size: 24px;
}

/* Landing variant */
.navbar--landing .company-name {
    font-size: 32px;
}

.navbar--landing .primary-btn {
    padding: 16px 32px;
}

.navbar--landing .nav-right {
    gap: 2.5rem;
}

@media (max-width: 960px) {
    .nav-right {
        gap: 1.5rem !important;
    }
}

@media (max-width: 860px) {
    .nav-right {
        gap: 1rem !important;
    }
}

/* Mobile responsive styles */
@media (max-width: 768px) {
    .navbar {
        padding: 0 1rem;
        height: 64px;
    }

    .navbar--landing {
        margin-top: 20px;
    }

    .navbar--compact {
        height: 64px;
    }

    .company-name {
        font-size: 24px !important;
    }

    /* Show mobile controls container */
    .mobile-controls {
        display: flex;
    }

    /* Show mobile sign-in button */
    .mobile-signin-btn {
        display: block;
    }

    /* Show mobile menu toggle */
    .mobile-menu-toggle {
        display: flex;
    }

    /* Hide nav-right by default on mobile */
    .nav-right {
        position: absolute;
        top: 100%;
        right: -1rem;
        left: -1rem;
        background: var(--background-color);
        flex-direction: column;
        gap: 1rem;
        padding: 1.5rem 3rem;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
        z-index: 999;
        min-height: 100vh;
    }

    .nav-right .primary-btn {
        max-width: 260px;
    }

    /* Show nav-right when mobile menu is open */
    .nav-right.mobile-open {
        opacity: 1;
        visibility: visible;
    }

    /* Adjust button sizing on mobile */
    .primary-btn {
        padding: 16px 20px !important;
        width: 100%;
        text-align: center;
        display: block;
    }

    /* Stack auth buttons vertically on mobile */
    .nav-right a,
    .nav-right .router-link-active {
        width: 100%;
        text-align: center;
        padding: 0.75rem 0;
    }

    .nav-right a:last-child,
    .nav-right .router-link-active:last-child {
        border-bottom: none;
    }
}

/* Prevent body scroll when mobile menu is open */
body.mobile-menu-open {
    overflow: hidden;
}

/* For very small screens */
@media (max-width: 480px) {
    .navbar {
        padding: 0;
    }

    .navbar.navbar--compact {
        padding-left: 20px !important;
    }

    .navbar--landing {
        margin-top: 0;
    }

    .company-name {
        font-size: 20px !important;
    }

    .nav-right {
        padding: 1rem;
        gap: 0.5rem !important;
    }
}
</style>